import { BASE_SPACE_URL } from "./sampleData";

/* ------------------------------------------------------------------ *
 * Product photos at the size the place needs.
 *
 * Product photos are camera originals (median 2.6 MB, ~3600x1800). The
 * server now keeps two WebP copies of each beside it and hands them out on
 * product listings as
 *
 *   image_variants: { thumb, medium, blur, width, height }
 *
 *   thumb   shorter side 480px — cards, grids, list rows, order lines
 *   medium  longer side 1200px — detail panels, the editor preview
 *   blur    a ~200-byte data: URL to paint before anything loads
 *
 * (Rays-Florist-Server docs/frontend-image-variants.md.) A product with no
 * copies yet — uploaded before, or not readable by the server — has no
 * `image_variants`; everything here falls back to `image`.
 *
 * Copies are only trusted when they belong to the product's CURRENT image:
 * the server names them "<image without extension>-thumb.webp" and
 * "-medium.webp". An editor that swaps the photo but still holds the old
 * product's `image_variants` therefore shows the new original, never the
 * old thumbnail.
 * ------------------------------------------------------------------ */

const stemOf = (key) => String(key).replace(/\.[^./]*$/, "");

const copyKey = (image, size) => `${stemOf(image)}-${size}.webp`;

/** The product's copies, or null when it has none for its current image. */
export function imageVariantsOf(product) {
  const image = product && product.image;
  const variants = product && product.image_variants;
  if (!image || !variants || typeof variants !== "object") return null;
  return variants.thumb === copyKey(image, "thumb") &&
    variants.medium === copyKey(image, "medium")
    ? variants
    : null;
}

/** URL of the full-size original, or "" when there's no photo. */
export function productImageOriginal(product) {
  return product && product.image ? BASE_SPACE_URL + product.image : "";
}

/**
 * URL of the photo at `size` ("thumb" | "medium"), falling back to the
 * original when there's no copy. "" when there's no photo.
 */
export function productImageSrc(product, size = "thumb") {
  const variants = imageVariantsOf(product);
  return variants && variants[size]
    ? BASE_SPACE_URL + variants[size]
    : productImageOriginal(product);
}

/**
 * Where the copy at `size` would be, for objects that carry `image` but no
 * `image_variants` — order lines loaded back from a saved order. It may not
 * exist (photo never had copies made), so only use it with an error
 * fallback; <ProductPhoto guess> does that.
 */
export function guessedImageSrc(product, size = "thumb") {
  return product && product.image
    ? BASE_SPACE_URL + copyKey(product.image, size)
    : "";
}

/** The inline blur preview (a data: URL), or "". */
export function productImageBlur(product) {
  const variants = imageVariantsOf(product);
  return (variants && variants.blur) || "";
}

/**
 * Inline style for a box that shows the photo as a CSS background: the copy
 * at `size` layered over its blur, so the box shows the photo's colours
 * straight away and sharpens when the copy arrives. The box's own CSS
 * (`background: … center / cover no-repeat`) applies to both layers.
 * undefined when there's no photo, so the caller's fallback shows.
 */
export function productImageStyle(product, size = "thumb") {
  const src = productImageSrc(product, size);
  if (!src) return undefined;
  const blur = productImageBlur(product);
  return {
    backgroundImage: blur ? `url("${src}"), url("${blur}")` : `url("${src}")`,
  };
}
